import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';

const RegisterModal = ({ isOpen, onClose, onSwitchToLogin, onRegisterSuccess }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showVerificationMessage, setShowVerificationMessage] = useState(false);
  const [verificationEmail, setVerificationEmail] = useState('');
  const [resendLoading, setResendLoading] = useState(false);
  const { register, loginWithGoogle, resendVerificationEmail } = useAuth();

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);

    const result = await register(email, password, name);

    if (result.success && result.requiresVerification) {
      setShowVerificationMessage(true);
      setVerificationEmail(email);
      setEmail('');
      setPassword('');
      setName('');
      setConfirmPassword('');
    } else if (result.success) {
      // Old flow - direct login (shouldn't happen with new implementation)
      onClose();
      setEmail('');
      setPassword('');
      setName('');
      setConfirmPassword('');
      // Call success callback if provided (only for direct login)
      if (onRegisterSuccess) {
        onRegisterSuccess();
      }
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  const handleResendVerification = async () => {
    setResendLoading(true);
    setError('');

    const result = await resendVerificationEmail(verificationEmail);
    
    if (result.success) {
      setError(''); // Clear any previous errors
      // You could show a success message here if needed
    } else {
      setError(result.error);
    }
    
    setResendLoading(false);
  };

  const handleBackToRegister = () => {
    setShowVerificationMessage(false);
    setVerificationEmail('');
    setError('');
  };

  const handleGoogleLogin = () => {
    loginWithGoogle();
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>×</button>
        
        {showVerificationMessage ? (
          // Email verification message
          <div className="verification-content">
            <div className="modal-header">
              <h2>Check Your Email</h2>
              <p>We've sent a verification link to <strong>{verificationEmail}</strong></p>
            </div>

            <div className="verification-message">
              <div className="email-icon">📧</div>
              <p>
                Please check your email and click the verification link to complete your registration.
                The link will expire in 24 hours.
              </p>
              
              {error && (
                <div className="error-message">
                  ⚠️ {error}
                </div>
              )}

              <div className="verification-actions">
                <button 
                  className="btn-resend" 
                  onClick={handleResendVerification}
                  disabled={resendLoading}
                >
                  {resendLoading ? 'Sending...' : 'Resend Email'}
                </button>
                
                <button 
                  className="btn-back" 
                  onClick={handleBackToRegister}
                >
                  ← Back to Registration
                </button>
              </div>
            </div>

            <div className="modal-footer">
              <p>
                Already verified?{' '}
                <button 
                  className="link-button" 
                  onClick={onSwitchToLogin}
                >
                  Login
                </button>
              </p>
            </div>
          </div>
        ) : (
          // Regular registration form
          <>
            <div className="modal-header">
              <h2>Create Account</h2>
              <p>Start creating amazing AI fashion videos</p>
            </div>

            {error && (
              <div className="error-message">
                ⚠️ {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="auth-form">
              <div className="form-group">
                <label htmlFor="register-name">Name</label>
                <input
                  id="register-name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your name"
                  required
                  disabled={loading}
                />
              </div>

              <div className="form-group">
                <label htmlFor="register-email">Email</label>
                <input
                  id="register-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  required
                  disabled={loading}
                />
              </div>

              <div className="form-group">
                <label htmlFor="register-password">Password</label>
                <input
                  id="register-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="At least 6 characters"
                  required
                  minLength={6}
                  disabled={loading}
                />
              </div>

              <div className="form-group">
                <label htmlFor="register-confirm-password">Confirm Password</label>
                <input
                  id="register-confirm-password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm your password"
                  required
                  disabled={loading}
                />
              </div>

              <button 
                type="submit" 
                className="btn-submit" 
                disabled={loading}
              >
                {loading ? 'Creating account...' : 'Sign Up'}
              </button>
            </form>

            <div className="divider">
              <span>OR</span>
            </div>

            <button 
              className="btn-google" 
              onClick={handleGoogleLogin}
              disabled={loading}
            >
              <span className="google-icon">G</span>
              Continue with Google
            </button>

            <div className="enhanced-modal-footer">
              <p>
                Already have an account?
                <button 
                  className="enhanced-link-button" 
                  onClick={onSwitchToLogin}
                  disabled={loading}
                >
                  Login
                </button>
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default RegisterModal;